import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useArtistAuth } from '../../contexts/ArtistAuthContext';
import {
  buildOpenInAppLaunchTarget,
  buildOpenInAppUrl,
  launchOpenInAppTarget,
} from '../../utils/openInAppLinks';
import '../../css/common/OpenInAppBanner.css';

const DISMISS_KEY = 'casticart:open-in-app-banner:dismissed-session';
// SP（モバイル）レイアウト幅。ResponsiveFooterNav.js の判定（max-width:700px）と揃える。
// この幅より広く戻したら案内は消える（matchMedia の change で即時反映）。
const SP_MEDIA_QUERY = '(max-width: 700px)';

const readDismissed = () => {
  if (typeof window === 'undefined') return true;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const writeDismissed = () => {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {}
};

// SP幅かどうかを matchMedia で判定し、幅の増減に即時追従する（広く戻したら false になる）。
const useIsSpWidth = () => {
  const [isSp, setIsSp] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(SP_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(SP_MEDIA_QUERY);
    const onChange = (event) => setIsSp(event.matches);
    setIsSp(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // 古い Safari 向けフォールバック。
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isSp;
};

export default function OpenInAppBanner({ enabled = true }) {
  const location = useLocation();
  const { user, checking: adminChecking } = useAuth();
  const { artistUser, checking: artistChecking } = useArtistAuth();
  const isSpWidth = useIsSpWidth();
  const [dismissed, setDismissed] = useState(readDismissed);

  const currentRole = location.pathname.startsWith('/admin/workspaces') || location.pathname.startsWith('/admin/settings')
    ? 'admin'
    : location.pathname.startsWith('/artist/workspaces') || location.pathname.startsWith('/artist/settings')
      ? 'artist'
      : null;
  const roleChecking =
    currentRole === 'admin'
      ? adminChecking
      : currentRole === 'artist'
        ? artistChecking
        : false;
  const isLoggedIn =
    currentRole === 'admin'
      ? Boolean(user?.isLoggedIn)
      : currentRole === 'artist'
        ? Boolean(artistUser?.isLoggedIn || artistUser?.userType === 'artist')
        : false;

  const appLink = useMemo(
    () =>
      buildOpenInAppUrl({
        pathname: location.pathname,
        search: location.search,
      }),
    [location.pathname, location.search]
  );
  const launchTarget = useMemo(() => buildOpenInAppLaunchTarget(appLink), [appLink]);

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  const handleOpenApp = (event) => {
    if (!launchTarget?.href) return;
    event.preventDefault();
    launchOpenInAppTarget(launchTarget);
  };

  if (
    !enabled ||
    dismissed ||
    !isSpWidth ||
    roleChecking ||
    !isLoggedIn ||
    !appLink?.href
  ) {
    return null;
  }

  return (
    <section className="open-in-app-banner" aria-label="モバイルアプリで開く">
      <div className="open-in-app-banner__body">
        <p className="open-in-app-banner__title">アプリの利用が便利です</p>
      </div>
      <div className="open-in-app-banner__actions">
        <a
          className="open-in-app-banner__primary"
          href={launchTarget?.href || appLink.href}
          onClick={handleOpenApp}
        >
          <span>アプリを開く</span>
        </a>
      </div>
      <button
        type="button"
        className="open-in-app-banner__close"
        onClick={handleDismiss}
        aria-label="アプリで開く案内を閉じる"
      >
        <i className="fa-solid fa-xmark" aria-hidden="true" />
      </button>
    </section>
  );
}
